import { registerLocaleData } from '@angular/common';
import localeHu from '@angular/common/locales/hu';
import { TranslocoService } from '@jsverse/transloco';
import { Injector, ModuleWithProviders, NgModule } from '@angular/core';
import {
  DialogService,
  DynamicDialogConfig,
  DynamicDialogRef,
} from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { PrimeNG } from 'primeng/config';
import { filter, pluck } from 'rxjs/operators';
import { TitleService } from 'src/services/title.service';
import { LogService } from 'src/services/log.service';
import { CommonService } from 'src/services/common.service';
import { FormsModule } from '@angular/forms';
import Helpers from 'src/shared/helpers';

export let InjectorInstance: Injector;

const modules = [FormsModule, ...Helpers.primeNgModules];

@NgModule({
  imports: modules,
  exports: modules,
  declarations: [],
  providers: [
    DynamicDialogRef,
    DynamicDialogConfig,
    DialogService,
    ConfirmationService,
    MessageService,
    CommonService,
    LogService,
    TitleService,
  ],
})
export class SharedModules {
  constructor(
    private translocoService: TranslocoService,
    private primeNGConfig: PrimeNG
  ) {
    registerLocaleData(localeHu, 'hu');
    this.setPrimeNgTranslationsOnChange();
  }

  static forRoot(): ModuleWithProviders<SharedModules> {
    return {
      ngModule: SharedModules,
    };
  }

  private setPrimeNgTranslationsOnChange(): void {
    this.translocoService.events$
      .pipe(
        filter((e) => e.type === 'translationLoadSuccess'),
        pluck('payload')
      )
      .subscribe(({ langName, scope }) => {
        this.primeNGConfig.setTranslation({
          startsWith: this.translocoService.translate('starts_with'),
          contains: this.translocoService.translate('contains'),
          notContains: this.translocoService.translate('not_contains'),
          endsWith: this.translocoService.translate('ends_with'),
          equals: this.translocoService.translate('equals'),
          notEquals: this.translocoService.translate('not_equals'),
          noFilter: this.translocoService.translate('no_filter'),
          lt: this.translocoService.translate('less_than'),
          lte: this.translocoService.translate('less_than_or_equal_to'),
          gt: this.translocoService.translate('greater_than'),
          gte: this.translocoService.translate('greater_than_or_equal_to'),
          is: this.translocoService.translate('is'),
          isNot: this.translocoService.translate('is_not'),
          before: this.translocoService.translate('before'),
          after: this.translocoService.translate('after'),
          clear: this.translocoService.translate('clear'),
          apply: this.translocoService.translate('apply'),
          matchAll: this.translocoService.translate('match_all'),
          matchAny: this.translocoService.translate('match_any'),
          addRule: this.translocoService.translate('add_rule'),
          removeRule: this.translocoService.translate('remove_rule'),
          accept: this.translocoService.translate('accept'),
          reject: this.translocoService.translate('reject'),
          choose: this.translocoService.translate('choose'),
          upload: this.translocoService.translate('upload'),
          cancel: this.translocoService.translate('cancel'),
          dayNames: this.translocoService.translate('day_names').split('|'),
          dayNamesShort: this.translocoService
            .translate('day_names_short')
            .split('|'),
          dayNamesMin: this.translocoService
            .translate('day_names_min')
            .split('|'),
          monthNames: this.translocoService.translate('month_names').split('|'),
          monthNamesShort: this.translocoService
            .translate('month_names_short')
            .split('|'),
          today: this.translocoService.translate('today'),
          weekHeader: this.translocoService.translate('week_header'),
          weak: this.translocoService.translate('weak'),
          medium: this.translocoService.translate('medium'),
          strong: this.translocoService.translate('strong'),
          passwordPrompt: this.translocoService.translate('password_prompt'),
          emptyMessage: this.translocoService.translate('empty_message'),
          emptyFilterMessage: this.translocoService.translate(
            'empty_filter_message'
          ),
          dateIs: this.translocoService.translate('date_is'),
          dateIsNot: this.translocoService.translate('date_is_not'),
          dateBefore: this.translocoService.translate('date_is_before'),
          dateAfter: this.translocoService.translate('date_is_after'),
          firstDayOfWeek: 1,
        });
      });
  }
}
